/**
 * Client-side mirror of the backend password policy.
 *
 * Keep this in lock-step with the server so the frontend never lets a user
 * submit a password the API will reject (or vice-versa).
 *
 * Policy:
 *   - Length 8–64 characters.
 *   - At least one of: lowercase letter, uppercase letter, number, symbol.
 *
 * Every character belongs to one of those four classes, so the class check is
 * satisfied by any non-empty password today — the binding rule in practice is
 * the 8–64 length. The class check is kept so the frontend stays structurally
 * in sync with the backend if the threshold is raised later.
 *
 * `PASSWORD_POLICY_MESSAGE` is intentionally verbatim with the server response
 * so the two never diverge. The `passwordPolicy` i18n key carries the same
 * English text plus a localized French translation for user-facing forms.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 64;
export const PASSWORD_POLICY_MESSAGE =
    'Password must be 8-64 characters and include at least one of: lowercase letter, uppercase letter, number, symbol.';

export function isValidPassword(password: string): boolean {
    if (!password) return false;
    if (password.length < PASSWORD_MIN_LENGTH || password.length > PASSWORD_MAX_LENGTH) {
        return false;
    }
    const classes =
        (/[a-z]/.test(password) ? 1 : 0) +
        (/[A-Z]/.test(password) ? 1 : 0) +
        (/[0-9]/.test(password) ? 1 : 0) +
        (/[^a-zA-Z0-9]/.test(password) ? 1 : 0);
    return classes >= 1;
}

/**
 * Per-requirement breakdown used by the password strength indicator. The
 * `length` flag mirrors the binding policy rule (8–64); the four class flags
 * surface the individual character classes so the UI can tick each one green.
 */
export interface PasswordCriteria {
    length: boolean;
    lowercase: boolean;
    uppercase: boolean;
    number: boolean;
    symbol: boolean;
}

export function getPasswordCriteria(password: string): PasswordCriteria {
    return {
        length: password.length >= PASSWORD_MIN_LENGTH && password.length <= PASSWORD_MAX_LENGTH,
        lowercase: /[a-z]/.test(password),
        uppercase: /[A-Z]/.test(password),
        number: /[0-9]/.test(password),
        symbol: /[^a-zA-Z0-9]/.test(password),
    };
}

export type PasswordStrength = 'empty' | 'weak' | 'fair' | 'good' | 'strong';

/**
 * Coarse strength label derived from how many requirements are satisfied.
 * Length is treated as a prerequisite: a password that is too short/long can
 * never rank above "fair" no matter how many classes it mixes.
 */
export function getPasswordStrength(password: string): PasswordStrength {
    if (!password) return 'empty';
    const criteria = getPasswordCriteria(password);
    const satisfied = [
        criteria.length,
        criteria.lowercase,
        criteria.uppercase,
        criteria.number,
        criteria.symbol,
    ].filter(Boolean).length;

    if (!criteria.length) return satisfied >= 3 ? 'fair' : 'weak';
    if (satisfied >= 5) return 'strong';
    if (satisfied === 4) return 'good';
    if (satisfied === 3) return 'fair';
    return 'weak';
}
